// User form modal component
// Form for creating new users with validation
// CHANGE: Added password visibility toggles with icons and text
// CHANGE: Added ReactDOM.createPortal to render modal at document root level
// CHANGE: Modified to support multiple site selection (many-to-many relationship)
// CHANGE: Standardized modal dimensions and added max-height with scrollable content
// CHANGE: Added click-outside-to-close and Escape key handlers
// CHANGE: Added ARIA attributes for accessibility
// CHANGE: Added dirty state detection and unsaved changes warning
// CHANGE: Removed manual password entry for new users - auto-generated temporary passwords only
// UPDATED: Migrated to centralized toast system for error messages
// UPDATED: Standardized button loading text to "En cours..."
// FIXED: Removed undefined error variable reference
// CHANGE: Refactored to use reusable Modal component instead of inline modal
// CHANGE: All roles now require at least one site (not just operator/manager)
// CHANGE: Auto-select SIEGE site for admin/director roles and prevent deselection
// CHANGE: Added visual indicator that SIEGE is required for admin/director

import { useState, useEffect } from 'react';
import type { Site, UserRole } from '../../lib/supabase';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { useToast } from '../../hooks/useToast';
import { Modal } from '../ui/Modal';
import { CustomSelect, type SelectOption } from '../ui/CustomSelect';

interface UserFormModalProps {
  sites: Site[];
  onClose: () => void;
  onSubmit: (data: {
    username: string;
    firstName: string;
    lastName: string;
    password: string;
    role: UserRole;
    siteIds?: string[];
  }) => Promise<{ success: boolean; error?: string }>;
  initialData?: {
    username: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    siteIds: string[];
  };
  isEditing?: boolean;
}

export function UserFormModal({ sites, onClose, onSubmit, initialData, isEditing = false }: UserFormModalProps) {
  const [username, setUsername] = useState(initialData?.username || '');
  const [firstName, setFirstName] = useState(initialData?.firstName || '');
  const [lastName, setLastName] = useState(initialData?.lastName || '');
  const [role, setRole] = useState<UserRole>(initialData?.role || 'operator');
  const [siteIds, setSiteIds] = useState<string[]>(initialData?.siteIds || []);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const { showToast } = useToast();

  // All roles now require at least one site
  const requiresSite = true;
  const isAdminOrDirector = role === 'admin' || role === 'director';

  // Store initial values for dirty state detection
  const initialUsername = initialData?.username || '';
  const initialFirstName = initialData?.firstName || '';
  const initialLastName = initialData?.lastName || '';
  const initialRole = initialData?.role || 'operator';
  const initialSiteIds = initialData?.siteIds || [];

  // Find SIEGE site
  const siegeSite = sites.find(site => site.name.toLowerCase() === 'siège');

  // Auto-select SIEGE site for admin/director roles
  useEffect(() => {
    if (isAdminOrDirector && siegeSite && !siteIds.includes(siegeSite.id)) {
      setSiteIds(prev => [siegeSite.id, ...prev]);
    }
  }, [role, siegeSite?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (requiresSite && siteIds.length === 0) {
      showToast('Veuillez sélectionner au moins un site', 'error');
      return;
    }

    // Show confirmation dialog
    setShowConfirmation(true);
  };

  const performUserCreation = async () => {
    setLoading(true);
    try {
      const result = await onSubmit({
        username,
        firstName,
        lastName,
        password: '', // Password will be auto-generated
        role,
        siteIds: siteIds // All roles now have sites
      });

      if (!result.success) {
        showToast(result.error || 'Une erreur est survenue', 'error');
      }
    } catch (err) {
      showToast('Une erreur est survenue', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setShowConfirmation(false);
    await performUserCreation();
  };

  const hasUnsavedChanges = (): boolean => {
    // For editing, check if any field changed
    if (isEditing) {
      if (username !== initialUsername) return true;
      if (firstName !== initialFirstName) return true;
      if (lastName !== initialLastName) return true;
      if (role !== initialRole) return true;
      if (JSON.stringify(siteIds.sort()) !== JSON.stringify(initialSiteIds.sort())) return true;
      return false;
    }

    // For creating, check if any field has been filled
    if (username.trim() !== '') return true;
    if (firstName.trim() !== '') return true;
    if (lastName.trim() !== '') return true;
    if (role !== 'operator') return true; // Different from default
    if (siteIds.length > 0) return true;

    return false;
  };

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesDialog(true);
    } else {
      onClose();
    }
  };

  // Handle Escape key for nested dialogs
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        if (showConfirmation) {
          setShowConfirmation(false);
        } else if (showUnsavedChangesDialog) {
          setShowUnsavedChangesDialog(false);
        } else {
          handleCloseAttempt();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [loading, showConfirmation, showUnsavedChangesDialog, username, firstName, lastName, role, siteIds]);

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title={isEditing ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}
        size="lg"
        loading={loading}
        hasUnsavedChanges={hasUnsavedChanges}
        onUnsavedChangesAttempt={() => setShowUnsavedChangesDialog(true)}
        closeOnEscape={false}
        ariaLabelledBy="user-form-title"
      >
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              Nom d'utilisateur *
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading || isEditing}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
            {isEditing && (
              <p className="text-xs text-gray-500 mt-1">Le nom d'utilisateur ne peut pas être modifié</p>
            )}
          </div>

          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              Prénom *
            </label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              Nom *
            </label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
              Rôle *
            </label>
            <CustomSelect
              value={role}
              onChange={(value) => setRole(value as any)}
              options={[
                { value: 'operator', label: 'Opérateur' },
                { value: 'manager', label: 'Manager' },
                { value: 'director', label: 'Directeur' },
                { value: 'admin', label: 'Administrateur' }
              ]}
              disabled={loading}
              placeholder="Sélectionner un rôle"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sites * (sélection multiple)
            </label>

            {isAdminOrDirector && siegeSite && (
              <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Note :</strong> Le site SIEGE est automatiquement assigné aux administrateurs et directeurs.
                </p>
              </div>
            )}

            <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto p-3 space-y-2 bg-white">
              {sites.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Aucun site disponible</p>
              ) : (
                sites.map((site) => {
                  const isSiegeAndAdminRole = isAdminOrDirector && siegeSite && site.id === siegeSite.id;
                  return (
                    <label
                      key={site.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={siteIds.includes(site.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSiteIds([...siteIds, site.id]);
                          } else {
                            // Prevent deselecting SIEGE for admin/director
                            if (!isSiegeAndAdminRole) {
                              setSiteIds(siteIds.filter(id => id !== site.id));
                            }
                          }
                        }}
                        disabled={loading || isSiegeAndAdminRole}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <span className={`text-sm flex-1 ${isSiegeAndAdminRole ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                        {site.name}
                        {isSiegeAndAdminRole && <span className="ml-2 text-xs">(requis)</span>}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            {siteIds.length > 0 && (
              <p className="text-xs text-gray-600 mt-2">
                {siteIds.length} site{siteIds.length > 1 ? 's' : ''} sélectionné{siteIds.length > 1 ? 's' : ''}
              </p>
            )}
            {siteIds.length === 0 && (
              <p className="text-xs text-red-600 mt-2">
                Veuillez sélectionner au moins un site
              </p>
            )}
          </div>

          {!isEditing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Mot de passe temporaire</strong><br />
                Un mot de passe temporaire sécurisé sera généré automatiquement. Vous pourrez le communiquer à l'utilisateur après la création du compte.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 mt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseAttempt}
                disabled={loading}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50"
              >
                {loading ? 'En cours...' : 'Enregistrer'}
              </button>
          </div>
        </form>
      </Modal>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        title={isEditing ? "Confirmer la modification" : "Confirmer la création"}
        message={isEditing
          ? `Êtes-vous sûr de vouloir modifier l'utilisateur "${username}" ?`
          : "Êtes-vous sûr de vouloir créer cet utilisateur ?"}
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirmation(false)}
        variant="info"
      />

      {/* Unsaved changes confirmation */}
      <ConfirmationDialog
        isOpen={showUnsavedChangesDialog}
        title="Modifications non enregistrées"
        message="Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?"
        confirmLabel="Quitter sans enregistrer"
        cancelLabel="Continuer à modifier"
        onConfirm={onClose}
        onCancel={() => setShowUnsavedChangesDialog(false)}
        variant="warning"
      />
    </>
  );
}
